import React, {createContext, useContext, useState, useEffect} from "react";
import axios from 'axios';
import jwt from "jsonwebtoken";
import moment from "moment";
import i18n from "i18next";

// Hooks
import useLocalStorage from "../common/hooks/use-local-storage";

const authContext = createContext();

const useAuth = () => {
    return useContext(authContext);
};

const getTokenFromLocalStorage = () => {
    return localStorage.getItem("ac-extranet.authToken");
};

const getHeaderJwt = (authHeader) => {
    if (authHeader) {
        const part = authHeader.split(' ');
        if (part.length === 2 && part[0] === 'Bearer') {
            return part[1];
        } else {
            return null;
        }
    } else {
        return null;
    }
};

// Function to check if token is expired
const isTokenExpired = (token) => {
    if (token) {
        const decoded = jwt.decode(token);
        if (decoded) {
            const tokenExp = decoded.exp,
                now = moment().unix().valueOf();
            return tokenExp - now <= 0;
        } else {
            return true;
        }
    } else {
        return true;
    }
}

// Function to check if user is logged in
const isLoggedInFn = (user, authToken) => {
    if (authToken) {
        return !!user && !isTokenExpired(authToken);
    } else {
        return false;
    }
};

const SIGNIN_URL = "/api/users/signin";
const WELCOME_PASSWORD_URL = "/api/users/welcome/";
const RESET_PASSWORD_URL = "/api/users/reset/";
const FORGOT_PASSWORD_URL = "/api/users/forgot";

const useProvideAuth = () => {

    // Local storage state
    const [user, setUser, removeUser] = useLocalStorage("ac-extranet.user");
    const [authToken, setAuthToken, removeAuthToken] = useLocalStorage("ac-extranet.authToken", true);
    const [selectedClient, setSelectedClient, removeSelectedClient] = useLocalStorage("ac-extranet.selectedClient", true);

    // Hook state
    const [loginError, setLoginError] = useState("");
    const [welcomeError, setWelcomeError] = useState("");
    const [forgotError, setForgotError] = useState("");
    const [forgotSuccess, setForgotSuccess] = useState("");
    const [showInputs, setShowInputs] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(isLoggedInFn(user, authToken));

    // Axios response interceptor, refresh token and force logout on status 401
    useEffect(() => {
        const responseInterceptor = axios.interceptors.response.use((response) => {
            if (response && response.config && response.config.url !== SIGNIN_URL) {
                const newToken = getHeaderJwt(response.headers.authorization);
                if (newToken && (newToken !== authToken)) {
                    setAuthToken(newToken);
                }
            }
            return response;
        }, (error) => {
            if ((error && error.response && error.response.config && error.response.config.url !== SIGNIN_URL) && error.response.status === 401) {
                removeUser();
                removeAuthToken();
            }
            return Promise.reject(error);
        });
        return () => axios.interceptors.request.eject(responseInterceptor);
    }, []);

    // Check is logged in when user or authToken changes
    useEffect(() => {
        setIsLoggedIn(isLoggedInFn(user, authToken));
    }, [user, authToken]);

    // If is logged in create interval to check is logged in each interval time
    useEffect(() => {
        if (isLoggedIn) {
            const checkIsLoggedIn = setInterval(() => {
                setIsLoggedIn(isLoggedInFn(user, authToken));
            }, 30000);

            return () => clearInterval(checkIsLoggedIn);
        }
    }, [isLoggedIn]);

    return {
        userId: (user && user._id) || "",
        userEmail: user?.email || "",
        userFullName: user?.full_name || "",
        userClientName: user?.client?.name || "",
        userType: (user && user.type) || "",
        features: user?.features || "",
        lang: user?.lang || "es",
        selectedClient,
        setSelectedClient,
        isLoggedIn,
        loginError,
        forgotError,
        forgotSuccess,
        welcomeError,
        showInputs,
        login: async (email, password) => {
            try {
                const response = await axios.post(SIGNIN_URL, {}, {
                    auth: {
                        username: email,
                        password: password
                    }
                });

                if (response && response.data && response.data.payload) {
                    const newUser = response.data.payload;
                    setUser(newUser);
                    setAuthToken(getHeaderJwt(response.headers.authorization));
                    if (newUser.type === "client") setSelectedClient(newUser.client._id);

                    //set lang
                    const lang = newUser.lang ? newUser.lang : "es";
                    await i18n.changeLanguage(lang);
                    setLoginError("");
                }
            } catch (error) {
                if (error && error.response && error.response.data){
                    const errorMessage = error.response.data.message;
                    if (errorMessage === "User not exist") {
                        setLoginError("El usuario no existe");
                    }
                    else if (errorMessage === "Invalid password") {
                        setLoginError("Contraseña incorrecta");
                    }
                    else {
                        setLoginError(errorMessage);
                    }
                }
            }
        },
        logout: async () => {
            try {

                // Server logout
                await axios.get('/api/users/signout', {
                    headers: { 'Authorization': 'Bearer ' + authToken }
                });

                // close chat
                window.$chatwoot.toggle("close");
                // reset chat
                window.$chatwoot.reset();

                // When server responses logout remove user, authToken and selectedClient from localStorage
                removeUser();
                removeAuthToken();
                removeSelectedClient();

            } catch (error) {}
        },
        welcomeUser: async (token, newPassword, verifyPassword) => {
            try {
                const response = await axios.post(WELCOME_PASSWORD_URL + token, {newPassword, verifyPassword}, {});

                if (response && response.data && response.data.payload) {
                    const newUser = response.data.payload;
                    if (newUser.type === "client") setSelectedClient(newUser.client._id);
                    setUser(newUser);
                    setAuthToken(getHeaderJwt(response.headers.authorization));
                    setLoginError("");
                }
            } catch (error) {
                if (error && error.response && error.response.data){
                    const errorMessage = error.response.data.message;
                    if (errorMessage === 'Token not validate to reset') {
                        setWelcomeError('Error, comprueba el enlace del correo para establecer la contraseña');
                    }
                    else if (errorMessage === 'Invalid password') {
                        setWelcomeError('Contraseña inválida');
                    }
                    else if (errorMessage === 'Password not validate to reset') {
                        setWelcomeError('No se puede cambiar la contraseña');
                    }
                    else {
                        setWelcomeError(errorMessage);
                    }
                }
            }
        },
        forgotPassword: async (email) => {
            try {
                const response = await axios.post(FORGOT_PASSWORD_URL, {email}, {});

                if (response && response.data && response.data.payload) {
                    setForgotError("");
                    setForgotSuccess("Se ha enviado el correo");
                    setShowInputs(false);
                }
            } catch (error) {
                if (error && error.response && error.response.data){
                    const errorMessage = error.response.data.message;
                    if (errorMessage === "User not found") {
                        setForgotError("El usuario no existe");
                        setForgotSuccess("");
                    }
                    else if (errorMessage === "Inactive User") {
                        setForgotError("El usuario está inactivo");
                        setForgotSuccess("");
                    }
                    else if (errorMessage === "User not exist") {
                        setForgotError("El usuario no existe");
                        setForgotSuccess("");
                    }
                    else {
                        setForgotError(errorMessage);
                    }
                }
            }
        },
        resetPassword: async (token, newPassword, verifyPassword) => {
            try {
                const response = await axios.post(RESET_PASSWORD_URL + token, {newPassword, verifyPassword}, {});

                if (response && response.data && response.data.payload) {
                    const newUser = response.data.payload;
                    if (newUser.type === "client") setSelectedClient(newUser.client);
                    setUser(newUser);
                    setAuthToken(getHeaderJwt(response.headers.authorization));
                }
            } catch (error) {
                if (error && error.response && error.response.data){
                    const errorMessage = error.response.data.message;
                    if (errorMessage === "User not found") {
                        setForgotSuccess("El usuario no existe");
                    }
                    else {
                        setForgotSuccess(errorMessage);
                    }
                }
            }
        }
    };
};

const ProvideAuth = ({children}) => {
    const auth = useProvideAuth();
    return (
        <authContext.Provider value={auth}>
            {children}
        </authContext.Provider>
    );
};

export {
    ProvideAuth,
    useAuth,
    getTokenFromLocalStorage
}